import { useTranslation } from "react-i18next"

import { useMutation as useApolloMutation } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { ensureArray } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { MutationOptions } from "shared/types/MutationOptions"
import { TeamMember } from "shared/types/__generated__/graphql"

import { useTeamLazyQuery } from "entities/teams/graphql/queries.generated"

import {
  CreateTeamMemberDocument,
  CreateTeamMemberMutation,
  CreateTeamMemberMutationVariables
} from "../graphql/mutations.generated"
import { CreateTeamMemberInput } from "../types/CreateTeamMemberInput"

export function useCreateTeamMember() {
  const { t } = useTranslation()
  const [apolloMutate] = useApolloMutation<
    CreateTeamMemberMutation,
    CreateTeamMemberMutationVariables
  >(CreateTeamMemberDocument)
  const [refetchTeam] = useTeamLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const { raiseLoadingToast: raiseSingleLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.TeamMember.CreateTeamMember.Start"),
      success: t("Request.TeamMember.CreateTeamMember.Success"),
      failure: t("Request.TeamMember.CreateTeamMember.Failure")
    }
  })

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.TeamMember.BatchCreateTeamMember.Start"),
      success: t("Request.TeamMember.BatchCreateTeamMember.Success"),
      failure: t("Request.TeamMember.BatchCreateTeamMember.Failure")
    }
  })

  const createSingleMember = useEvent(
    async (
      {
        teamID,
        teamMember
      }: {
        teamMember: CreateTeamMemberInput
        teamID: string
      },
      options?: MutationOptions
    ): Promise<ApiResponse<TeamMember>> => {
      const { raiseSuccessToast, raiseErrorToast } = raiseSingleLoadingToast({
        suppress: options?.suppressToasts
      })

      return callMutation<
        CreateTeamMemberMutation,
        CreateTeamMemberMutationVariables,
        TeamMember
      >(apolloMutate, {
        variables: {
          input: teamMember,
          teamID
        } as CreateTeamMemberMutationVariables,
        transform: data => data.teamMember as TeamMember,
        apolloOptions: {
          onCompleted: () => {
            raiseSuccessToast()
          },
          onError: () => {
            raiseErrorToast()
          }
        }
      })
    }
  )

  const handleCreateTeamMembers = useEvent(
    async (
      {
        teamMembers,
        teamID
      }: {
        teamMembers: CreateTeamMemberInput | CreateTeamMemberInput[]
        teamID: string
      },
      options?: { suppressToasts?: boolean }
    ): Promise<ApiResponse<TeamMember[]>> => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: !!options?.suppressToasts
      })

      let response: ApiResponse<TeamMember[]>
      try {
        const results = await Promise.all(
          ensureArray(teamMembers).map(member =>
            createSingleMember(
              { teamMember: member, teamID },
              { ...options, suppressToasts: true }
            )
          )
        )

        const allSucceeded = results.every(result => !result.errors?.length)

        if (allSucceeded) {
          response = {
            data: results.map(result => result.data as TeamMember)
          }
          raiseSuccessToast()
        } else {
          response = {
            data: null,
            errors: results.flatMap(result => result.errors || [])
          }
          raiseErrorToast()
        }
      } catch {
        response = {
          data: null,
          errors: [
            {
              message: t("Request.TeamMember.BatchCreateTeamMember.Failure")
            }
          ]
        }
      } finally {
        refetchTeam({ variables: { id: teamID } })
      }

      if (!response.errors?.length) {
        raiseSuccessToast()
      } else {
        raiseErrorToast()
      }

      return response
    }
  )

  return {
    createTeamMember: createSingleMember,
    createMultipleTeamMembers: handleCreateTeamMembers
  }
}
